/*
 * Keetsa sticky add-to-cart bar (KEET-442).
 *
 * Mirrors the main product section instead of owning a form:
 *  - directional IntersectionObserver shows the bar once the main ATC scrolls above the viewport;
 *  - subscribes to variantChange to mirror the price (from the fetched section HTML), the static
 *    variant label, the in-bar select value, and the submit button state;
 *  - on single-option products, the in-bar <select> drives the real <variant-selects> picker;
 *  - clicking the bar button clicks the real #ProductSubmitButton-{section} so add-on, Rebuy and
 *    cart-drawer behavior is reused.
 */
if (!customElements.get('keetsa-sticky-atc')) {
  customElements.define(
    'keetsa-sticky-atc',
    class KeetsaStickyAtc extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.select = this.querySelector('[data-sticky-atc-select]');
        this.submitButton = this.querySelector('[data-sticky-atc-submit]');
        this.submitButtonText = this.querySelector('[data-sticky-atc-submit-text]');
        this.priceContainer = this.querySelector('[data-sticky-atc-price]');
        this.variantLabel = this.querySelector('[data-sticky-atc-variant-label]');
        this.bundleDriverPosition = parseInt(this.dataset.bundleDriverPosition, 10) || 0;
        this.pendingRefocus = false;

        this._onSelectChange = this.onSelectChange.bind(this);
        this._onSubmitClick = this.onSubmitClick.bind(this);
        this._onVariantChange = this.onVariantChange.bind(this);
        this._onBreakpointChange = this.updateVisibility.bind(this);
      }

      /**
       * KEET-559 — the label a bundle should show. A bundle's variant title is every
       * component's size joined, which does not fit the bar and says nothing the driver
       * value does not. Falls back to the full title whenever no driver is configured,
       * which is every non-bundle product, so nothing else changes.
       */
      labelFor(variant) {
        if (!variant) return '';
        if (this.bundleDriverPosition >= 1) {
          const options = variant.options || [variant.option1, variant.option2, variant.option3];
          const value = options[this.bundleDriverPosition - 1];
          if (value) return value;
        }
        return variant.title ?? '';
      }

      connectedCallback() {
        // Skip inside quick-add modals: it would be a duplicate instance sharing the global body
        // class, and its disconnect could strip that class from the real PDP bar.
        if (this.closest('quick-add-modal')) return;
        if (!this.sectionId) return;

        const realButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.observedTarget = realButton?.closest('.product-form__buttons') || realButton;
        if (!this.observedTarget) return;

        this._initialized = true;
        this.syncButtonState();

        this.select?.addEventListener('change', this._onSelectChange);
        this.submitButton?.addEventListener('click', this._onSubmitClick);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange);
        }

        if ('IntersectionObserver' in window) {
          this.atcScrolledPast = false;
          this.footerInView = false;
          // Footer-hide applies only to the bottom bar (mobile/tablet, < 990px).
          this.bottomBarQuery = window.matchMedia('(max-width: 989px)');
          this.bottomBarQuery.addEventListener('change', this._onBreakpointChange);

          this.observer = new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              if (!entry) return;
              this.atcScrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
              this.updateVisibility();
            },
            { threshold: 0 }
          );
          this.observer.observe(this.observedTarget);

          // Hide the bar while the footer is in view so the bottom bar never covers footer links.
          const footer = document.querySelector('.footer');
          if (footer) {
            this.footerObserver = new IntersectionObserver(
              (entries) => {
                const entry = entries[0];
                if (!entry) return;
                this.footerInView = entry.isIntersecting;
                this.updateVisibility();
              },
              { threshold: 0 }
            );
            this.footerObserver.observe(footer);
          }
        }
      }

      disconnectedCallback() {
        // Only a fully-initialized instance cleans up (skips quick-add/early-return instances so they
        // never touch the shared body class).
        if (!this._initialized) return;
        this.observer?.disconnect();
        this.footerObserver?.disconnect();
        this.bottomBarQuery?.removeEventListener('change', this._onBreakpointChange);
        this.select?.removeEventListener('change', this._onSelectChange);
        this.submitButton?.removeEventListener('click', this._onSubmitClick);
        this.variantChangeUnsubscriber?.();
        document.body.classList.remove('keetsa-sticky-atc-open');
      }

      toggleVisible(visible) {
        this.classList.toggle('is-visible', visible);
        // Flag on <body> (theme-owned) so CSS can lift fixed third-party widgets (chat, reviews)
        // above the bottom bar on mobile — no mutation of the widgets' own DOM.
        document.body.classList.toggle('keetsa-sticky-atc-open', visible);
        if (visible) {
          this.removeAttribute('inert');
          this.removeAttribute('aria-hidden');
        } else {
          this.setAttribute('inert', '');
          this.setAttribute('aria-hidden', 'true');
        }
      }

      updateVisibility() {
        // Footer-hide is for the bottom bar (mobile/tablet, < 990px) only. The desktop top bar
        // sits below the header and never overlaps the footer, so it stays visible once the main
        // ATC is scrolled past.
        const hideForFooter = this.bottomBarQuery?.matches && this.footerInView;
        this.toggleVisible(this.atcScrolledPast && !hideForFooter);
      }

      onSelectChange(event) {
        const target = event?.target;
        if (!target) return;

        const picker = document.getElementById(`variant-selects-${this.sectionId}`);
        if (!picker) return;

        const value = target.value;
        const mainSelect = picker.querySelector('select[name^="options"]');
        if (mainSelect) {
          mainSelect.value = value;
          mainSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          const radios = picker.querySelectorAll('input[type="radio"]');
          for (const radio of radios) {
            if (radio.value === value) {
              radio.checked = true;
              radio.dispatchEvent(new Event('change', { bubbles: true }));
              break;
            }
          }
        }
        this.pendingRefocus = true;
      }

      onSubmitClick() {
        const button = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!button) return;
        if (button.hasAttribute('disabled') || button.getAttribute('aria-disabled') === 'true') return;
        button.click();
      }

      onVariantChange(event) {
        const data = event?.data;
        if (!data || data.sectionId !== this.sectionId) return;

        // Trusted first-party markup from the Section Rendering API (same source and pattern as
        // product-info.js's own price update) — not user input, so innerHTML is not an XSS surface.
        // KEET-593: prefer this bar's own re-rendered price (rendered without the currency code);
        // the main price block is the fallback if the fetched section somehow lacks the bar.
        const newPrice =
          data.html?.querySelector?.(`keetsa-sticky-atc[data-section-id="${this.sectionId}"] [data-sticky-atc-price]`) ??
          data.html?.getElementById?.(`price-${this.sectionId}`);
        if (newPrice && this.priceContainer) {
          this.priceContainer.innerHTML = newPrice.innerHTML;
        }

        if (data.variant) {
          if (this.variantLabel) this.variantLabel.textContent = this.labelFor(data.variant);
          if (this.select && data.variant.option1 != null) this.select.value = data.variant.option1;
        }

        this.syncButtonState();

        if (this.pendingRefocus && this.select) {
          this.pendingRefocus = false;
          requestAnimationFrame(() => {
            // Don't yank focus to a bar that scrolled out (inert) or detached mid-fetch.
            if (this.isConnected && !this.hasAttribute('inert')) this.select.focus();
          });
        }
      }

      syncButtonState() {
        const realButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!realButton || !this.submitButton) return;

        this.submitButton.disabled = realButton.hasAttribute('disabled');

        const text = realButton.querySelector('span')?.textContent?.trim();
        if (text && this.submitButtonText) this.submitButtonText.textContent = text;
      }
    }
  );
}
